import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import Analytics from '../../../../analytics/Analytics';
import { modelOf } from '../../../../prop-types';
import AdStore from '../../../../store/AdStore';
import ConfigStore from '../../../../store/ConfigStore';
import UIStore from '../../../../store/UIStore';
import AdZones from '../../../../types/AdZones';
import RequestState from '../../../../types/RequestState';
import BannerType from '../../../../types/banner/BannerType';
import SSRAwareLazyLoad from '../../../common/SSRAwareLazyLoad';
import CommonSlider from '../../../common/Slider';
import AdLoaderSkeleton from '../../../skeleton/AdLoaderSkeleton';
import AdLoaded from '../../AdLoaded';
import AdLoader from '../../AdLoader';
import Banner from '../../Banner';
import SingleSlideLayout from '../SingleSlideLayout';
import BannerSliderStyles, {
  countMainAxisSize,
  countMinCrossAxisSize,
} from '../Styles';
import { AUTOPLAY_SPEED } from './constants';
import useBannerSliderPlayback from './hooks/useBannerSliderPlayback';
import useBannerSliderSettings from './hooks/useBannerSliderSettings';
import useBannerSliderSizing from './hooks/useBannerSliderSizing';

const BannerSlider = ({
  adStore,
  configStore,
  uiStore,
  analytics,
  className,
  searchParams,
  maximumCrossAxisSize,
  overrideSettings,
  preLoadedAds,
  aspectRatio,
  adZone,
}) => {
  const id = useCallback(uniqueId('BannerSlider--'), []);

  const { getSettings } = useBannerSliderSettings({
    overrideSettings,
    autoplaySpeed: AUTOPLAY_SPEED,
  });

  const bannerSliderRef = useRef(null);
  const placeholderRef = useRef(null);
  const sliderRef = useRef(null);
  const sentPromotionEventsRef = useRef([]);
  const [elementWidth, setElementWidth] = useState(0);

  const shouldResetToFirstSlide = useCallback(
    (slideCount, settings) => {
      return (
        slideCount > 1 &&
        !!settings &&
        !settings.infinite &&
        !!settings.centerMode &&
        [AdZones.MAIN_BANNER, AdZones.FOOTER_BANNER].includes(adZone)
      );
    },
    [adZone]
  );

  const handleResize = () => {
    if (!bannerSliderRef.current) {
      return null;
    }

    if (elementWidth !== bannerSliderRef.current.clientWidth) {
      setElementWidth(bannerSliderRef.current.clientWidth);
    }
  };

  const playback = useBannerSliderPlayback({
    sliderRef,
    bannerSliderRef,
    autoplaySpeed: AUTOPLAY_SPEED,
    shouldResetToFirstSlide,
  });

  const sendPromotionView = useCallback(
    (slideIndex, slides) => {
      const banner = slides[slideIndex];

      if (!banner || sentPromotionEventsRef.current.includes(slideIndex)) {
        return;
      }

      analytics.promoView([
        {
          bannerZone: adZone,
          banner,
        },
      ]);
      sentPromotionEventsRef.current = [
        ...sentPromotionEventsRef.current,
        slideIndex,
      ];
    },
    [adZone, analytics]
  );

  const getSliderEvents = (slides) => {
    const events = {
      beforeChange: playback.onBannerBeforeChange,
      afterChange: (currentSlide) => {
        playback.onBannerAfterChange(currentSlide);

        if (configStore.analytics.ga4.enabled) {
          sendPromotionView(currentSlide, slides);
        }
      },
    };

    if (configStore.analytics.ga4.enabled) {
      events.onInit = () => {
        slides.length > 0 && sendPromotionView(0, slides);
      };
    }

    return events;
  };

  const getScentHeight = () => {
    return configStore.getScentHeight(uiStore.isMobile);
  };

  const getMaximumDefaultHeight = () => {
    const parentHeight =
      bannerSliderRef.current && elementWidth > 0
        ? countMainAxisSize(elementWidth, aspectRatio)
        : maximumCrossAxisSize;

    return (
      countMinCrossAxisSize(maximumCrossAxisSize, parentHeight) +
      getScentHeight()
    );
  };

  const getSlides = (ads) => {
    return [...ads].filter((banner) => {
      return (
        (uiStore.isSmallest && !banner.hide_from_mobile_users) ||
        (!uiStore.isSmallest && !banner.hide_from_desktop_tablet_users)
      );
    });
  };

  const { hasMobileSquareMode, isPortraitVideoSlide, getSlideWidth } =
    useBannerSliderSizing({
      uiStore,
      aspectRatio,
      maximumCrossAxisSize,
      elementWidth,
      countMainAxisSize,
      countMinCrossAxisSize,
    });

  const shouldRenderSingleSlideLayout = useCallback(
    (slides, settings) => {
      return (
        slides.length === 1 &&
        !!overrideSettings?.centerMode &&
        !settings.vertical &&
        [AdZones.MAIN_BANNER, AdZones.FOOTER_BANNER].includes(adZone)
      );
    },
    [adZone, overrideSettings]
  );

  const shouldUseVerticalWideImageLetterbox = useCallback(
    (banner, settings) => {
      if (
        !settings.vertical ||
        banner.type === BannerType.VIDEO ||
        banner.image_width <= 0 ||
        banner.image_height <= 0
      ) {
        return false;
      }

      return banner.image_width / banner.image_height > aspectRatio;
    },
    [aspectRatio]
  );

  const renderBannerSlide = useCallback(
    (banner, settings, mobileSquareMode) => (
      <div
        key={banner.id}
        className={classNames('BannerSlider__slide', {
          'BannerSlider__slide--mobile-letterbox': mobileSquareMode,
          'BannerSlider__slide--portrait-video':
            mobileSquareMode && isPortraitVideoSlide(banner),
          'BannerSlider__slide--vertical-image-letterbox':
            shouldUseVerticalWideImageLetterbox(banner, settings),
        })}
        style={
          settings.variableWidth
            ? {
                width: getSlideWidth(banner, mobileSquareMode),
              }
            : undefined
        }
      >
        <Banner
          banner={banner}
          bannerZone={adZone}
          onVideoPlaybackChange={playback.handleVideoPlaybackChange}
        />
      </div>
    ),
    [
      adZone,
      getSlideWidth,
      isPortraitVideoSlide,
      playback,
      shouldUseVerticalWideImageLetterbox,
    ]
  );

  const shouldForceVariableWidth = useCallback(
    (slides, settings, mobileSquareMode) => {
      if (
        settings.vertical ||
        settings.variableWidth ||
        slides.length <= 1 ||
        ![
          AdZones.PRODUCT_PAGE_BANNER_1,
          AdZones.PRODUCT_PAGE_BANNER_4,
        ].includes(adZone)
      ) {
        return false;
      }

      const hostWidthBudget =
        elementWidth > 0
          ? elementWidth
          : countMainAxisSize(maximumCrossAxisSize, aspectRatio);

      return slides.some(
        (banner) => getSlideWidth(banner, mobileSquareMode) > hostWidthBudget
      );
    },
    [adZone, aspectRatio, elementWidth, getSlideWidth, maximumCrossAxisSize]
  );

  const getTrackMinWidth = useCallback(
    (slides, settings, mobileSquareMode) => {
      if (
        slides.length <= 1 ||
        settings.vertical ||
        settings.infinite ||
        !settings.variableWidth
      ) {
        return null;
      }

      return slides.reduce((totalWidth, banner) => {
        return totalWidth + getSlideWidth(banner, mobileSquareMode);
      }, 0);
    },
    [getSlideWidth]
  );

  const getAds = () => (ads) => {
    if (!ads || ads.length === 0) {
      return null;
    }

    const slides = getSlides(ads);
    const baseSettings = getSettings(slides);
    const mobileSquareMode = hasMobileSquareMode(slides, {
      isVertical: baseSettings.vertical,
    });
    const settings = mobileSquareMode
      ? {
          ...baseSettings,
          centerMode: false,
        }
      : baseSettings;
    const effectiveSettings = shouldForceVariableWidth(
      slides,
      settings,
      mobileSquareMode
    )
      ? {
          ...settings,
          variableWidth: true,
        }
      : settings;
    const trackMinWidth = getTrackMinWidth(
      slides,
      effectiveSettings,
      mobileSquareMode
    );
    playback.setLastSlideCount(slides.length, effectiveSettings);
    const sliderEvents = getSliderEvents(slides);

    return (
      <div
        className={classNames('BannerSlider', className)}
        id={id}
        ref={bannerSliderRef}
        onMouseEnter={playback.onInteractionStart}
        onMouseLeave={playback.onInteractionEnd}
        onTouchStart={playback.onInteractionStart}
        onTouchEnd={playback.onInteractionEnd}
      >
        {shouldRenderSingleSlideLayout(slides, effectiveSettings) ? (
          <SingleSlideLayout
            adZone={adZone}
            banner={slides[0]}
            mobileSquareMode={mobileSquareMode}
            onVideoPlaybackChange={playback.handleVideoPlaybackChange}
            onVisible={
              configStore.analytics.ga4.enabled
                ? () => sendPromotionView(0, slides)
                : undefined
            }
            isPortraitVideoSlide={isPortraitVideoSlide(slides[0])}
            slideWidth={getSlideWidth(slides[0], mobileSquareMode)}
            variableWidth={effectiveSettings.variableWidth}
          />
        ) : (
          <CommonSlider
            ref={sliderRef}
            {...effectiveSettings}
            {...sliderEvents}
          >
            {slides.map((banner) =>
              renderBannerSlide(banner, effectiveSettings, mobileSquareMode)
            )}
          </CommonSlider>
        )}
        <ReactResizeDetector
          handleWidth
          onResize={handleResize}
          refreshMode="throttle"
          refreshRate={1000}
        />
        <BannerSliderStyles
          bannerSliderId={id}
          elementWidth={elementWidth}
          bannerSlider={bannerSliderRef.current}
          aspectRatio={aspectRatio}
          maximumCrossAxisSize={maximumCrossAxisSize}
          ifVertical={effectiveSettings.vertical}
          slidesToShow={effectiveSettings.slidesToShow}
          variableWidth={effectiveSettings.variableWidth}
          mobileSquareMode={mobileSquareMode}
          trackMinWidth={trackMinWidth}
        />
      </div>
    );
  };

  if (preLoadedAds && preLoadedAds.length > 0) {
    return <AdLoaded ads={preLoadedAds}>{getAds()}</AdLoaded>;
  }

  const getParentHeight = () => {
    return bannerSliderRef.current && elementWidth > 0
      ? null
      : { style: { height: getMaximumDefaultHeight() } };
  };

  const ads = adStore.getAdsByAdZone(searchParams, adZone);
  if (
    adStore.getAdQueryStateBySearchParameters(searchParams) ===
      RequestState.LOADED &&
    ads?.length === 0
  ) {
    return null;
  }

  return (
    <SSRAwareLazyLoad
      once
      offset={50}
      height={getMaximumDefaultHeight()}
      ref={placeholderRef}
      {...getParentHeight()}
    >
      <AdLoader
        placeHolder={<AdLoaderSkeleton />}
        searchParams={searchParams}
        adZone={adZone}
      >
        {getAds()}
      </AdLoader>
    </SSRAwareLazyLoad>
  );
};

BannerSlider.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  aspectRatio: PropTypes.number.isRequired,
  searchParams: PropTypes.object.isRequired,
  adZone: PropTypes.string.isRequired,
  preLoadedAds: PropTypes.array,
  className: PropTypes.string,
  maximumCrossAxisSize: PropTypes.number,
  overrideSettings: PropTypes.object,
};

BannerSlider.defaultProps = {
  maximumCrossAxisSize: 400,
};

export default inject(
  'adStore',
  'configStore',
  'uiStore',
  'analytics'
)(observer(BannerSlider));
